import { Component, Inject, inject, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { ContentHeader } from '../layout/components/content-header/content-header.component';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { HeaderValueGetterParams } from 'ag-grid-community';
import { TranslateService } from '@ngx-translate/core';
import { UtilityTranslateService } from '../server/helpers/translate-service';
import { map, take } from 'rxjs/operators';
import { AuthenticationService } from 'app/server/services';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { takeUntil } from 'rxjs/operators';

import { TableStateService } from '../@core/services/table-state.service';

import { FormControl, ValidatorFn } from '@angular/forms';
import { FormGroupPro } from './form-group-pro';
import { AppLanguage, APP_LANGUAGES, DEFAULT_LANGUAGE } from '../config/languages.config';
import { VocabularyService } from '../server/services/vocabulary.service';

export { AppLanguage, APP_LANGUAGES } from '../config/languages.config';

@Component({
	template: '',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class BaseComponent implements OnDestroy {
	_unsubscribeAll: Subject<any>;

	contentHeader: ContentHeader;
	public _translateService = inject(TranslateService);
	private _activatedRoute = inject(ActivatedRoute, { optional: true });
	public _utilityTranslateService = inject(UtilityTranslateService);
	private authService = inject(AuthenticationService);
	public _tableStateService = inject(TableStateService);
	protected _vocabularyService = inject(VocabularyService);

	public activeLang: string = DEFAULT_LANGUAGE.code;
	/** Only the languages the backend actually has vocabulary/translation data for (single source of truth). */
	public activeLanguages: AppLanguage[] = APP_LANGUAGES.filter(l => this._vocabularyService.supportedLangCodes.includes(l.code));

	public setLang(lang: string): void {
		this.activeLang = lang;
	}

	/**
	 * Build dynamic form controls for each translatable field across all active non-default languages.
	 * e.g. buildTranslationControls({ name: [Validators.minLength(1)] })
	 * returns { name_en: new FormControl(...), name_es: ... }
	 */
	public buildTranslationControls(
		fieldsConfig: Record<string, ValidatorFn | ValidatorFn[] | null> | string[]
	): Record<string, FormControl> {
		const controls: Record<string, FormControl> = {};
		const nonDefaultLangs = this.activeLanguages.filter(l => !l.isDefault);

		const fields: Record<string, ValidatorFn | ValidatorFn[] | null> = Array.isArray(fieldsConfig)
			? fieldsConfig.reduce((acc, f) => ({ ...acc, [f]: null }), {})
			: fieldsConfig;

		for (const lang of nonDefaultLangs) {
			for (const [field, validators] of Object.entries(fields)) {
				const controlName = `${field}_${lang.code}`;
				controls[controlName] = new FormControl(null, validators || []);
			}
		}
		return controls;
	}

	/**
	 * Resolves the per-entity vocabulary translation (keyed by the record's UUID, uppercased — see
	 * DynamicTranslationService on the backend) for each item's `name`, falling back to the original
	 * value when no translation exists for the current language. Reactive to language changes, the
	 * same way TranslateRendererComponent resolves translations for core-table columns.
	 */
	public translateEntityNames<T extends { id: string; name: string }>(items: T[]): Observable<T[]> {
		if (items.length === 0) {
			return of(items);
		}

		const ids = items.map(item => item.id.toUpperCase());

		return this._translateService.stream(ids).pipe(
			map(translations =>
				items.map(item => {
					const key = item.id.toUpperCase();
					const translated = translations[key];
					return translated && translated !== key ? { ...item, name: translated } : item;
				})
			)
		);
	}

	/**
	 * Same as `translateEntityNames()`, but for call sites that need a single resolved value instead
	 * of a live stream — typically anything combined with `forkJoin`/`switchMap` as part of a one-shot
	 * data load. `translateEntityNames()` is built on `TranslateService.stream()`, which never completes
	 * (it stays open to re-emit on every future language change, for callers that keep a long-lived
	 * `takeUntil` subscription) — `forkJoin` waits for every source to COMPLETE before combining results,
	 * so feeding it `translateEntityNames()` directly hangs forever with no error. This wrapper's explicit
	 * name makes that choice visible at the call site instead of relying on a bare `.pipe(take(1))`.
	 */
	public translateEntityNamesOnce<T extends { id: string; name: string }>(items: T[]): Observable<T[]> {
		return this.translateEntityNames(items).pipe(take(1));
	}

	/**
	 * Extracts all translations from form value into { [lang]: { [field]: value } }
	 * and removes the temporary `${field}_${lang}` properties from the payload.
	 */
	public preparePayloadWithTranslations<T = any>(formValue: any, fields: string[]): T {
		const payload = { ...formValue };
		const translations: Record<string, Record<string, any>> = {};
		const nonDefaultLangs = this.activeLanguages.filter(l => !l.isDefault);

		for (const lang of nonDefaultLangs) {
			translations[lang.code] = {};
			for (const field of fields) {
				const key = `${field}_${lang.code}`;
				const val = payload[key];
				translations[lang.code][field] = typeof val === 'string' ? val.trim() || null : (val ?? null);
				delete payload[key];
			}
		}

		payload.translations = translations;
		return payload as T;
	}

	/**
	 * Patches translation form controls from entity.translations object
	 */
	public patchTranslations(form: FormGroupPro, translations: Record<string, any> | undefined, fields: string[]): void {
		if (!form) return;
		const nonDefaultLangs = this.activeLanguages.filter(l => !l.isDefault);

		for (const lang of nonDefaultLangs) {
			for (const field of fields) {
				const val = translations?.[lang.code]?.[field] ?? null;
				const control = form.get(`${field}_${lang.code}`);
				if (control) {
					control.patchValue(val);
				}
			}
		}
	}

	protected restoreColumnFilters(tableName: string): void {
		const savedState = this._tableStateService.getState(tableName);
		if (savedState?.columnFilters) {
			(this as any).columnFilters = { ...savedState.columnFilters };
		}
	}

	protected saveColumnFilters(tableName: string): void {
		if ((this as any).columnFilters) {
			this._tableStateService.saveState(tableName, { columnFilters: (this as any).columnFilters });
		}
	}

	public noteConfigLabels: { [key: string]: string } = {};
	public documentConfigLabels: { [key: string]: string } = {};

	constructor(protected location?: Location) {
		this._unsubscribeAll = new Subject<any>();
		this.updateConfigLabels();

		this.contentHeader = {
			headerTitle: '',
			icon: undefined,
			actionButton: false,
			btn: {
				visible: false,
				disabled: false,
				textDisabled: undefined,
				routerLink: 'create',
			},
		};

		this._translateService.onLangChange.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => this.updateConfigLabels());
	}

	setHeader(
		title: string | undefined,
		icon: IconProp | Array<IconProp>,
		btn: {
			visible: boolean;
			disabled?: boolean;
			textDisabled?: string;
			type?: string;
			text?: string;
			routerLink?: string;
			permission?: string | string[];
		},
		breadcrumb?: {
			type?: string;
			links?: Array<{
				name?: string;
				isLink?: boolean;
				link?: string;
			}>;
		}
	) {
		this.contentHeader.headerTitle = title;
		this.contentHeader.icon = icon;
		this.contentHeader.btn = { ...this.contentHeader.btn, ...btn };
		const resolvedBreadcrumb = breadcrumb ?? (btn.type === 'back' ? undefined : this.defaultIndexBreadcrumb(title));
		if (resolvedBreadcrumb) {
			this.contentHeader.breadcrumb = resolvedBreadcrumb;
		}
	}

	/**
	 * Breadcrumb "Home > pagina" per tutte le pagine indice di scuola e ateneo (un solo segmento dopo
	 * /university o /university/school); le pagine di creazione e modifica passano il proprio breadcrumb.
	 */
	private defaultIndexBreadcrumb(title: string | undefined) {
		const path = (this._activatedRoute?.snapshot?.pathFromRoot ?? [])
			.flatMap(route => route.url.map(segment => segment.path))
			.join('/');
		const match = /^university\/(school\/)?([a-z-]+)$/.exec(path);
		if (!match || ['home', 'school'].includes(match[2]) || !title) {
			return undefined;
		}
		return {
			type: '',
			links: [
				{ name: this._translateService.instant('common_breadcrumb_home'), isLink: true, link: `/university/${match[1] ? 'school/' : ''}home` },
				{ name: title, isLink: false },
			],
		};
	}

	setActionButtonHeader(visible: boolean) {
		this.contentHeader.actionButton = visible;
	}

	localizeHeader(parameters: HeaderValueGetterParams): string {
		const headerIdentifier = parameters.colDef?.headerName || '';
		return this._translateService.instant(headerIdentifier);
	}

	public crudSectionVisible(crudSectionId: string, section: 'read' | 'update' | 'create' | 'delete') {
		return true;
	}

	ngOnDestroy() {
		this._unsubscribeAll.next(null);
		this._unsubscribeAll.complete();
	}

	turnBack(): void {
		if (this.location) {
			this.location.back();
		}
	}

	public getActivityConfigs$(): Observable<Array<{ name: string; value: string }>> {
		return this._translateService.stream(['common_active', 'common_required', 'common_disabled']).pipe(
			map(translations => [
				{ name: translations['common_active'], value: 'active' },
				{ name: translations['common_required'], value: 'required' },
				{ name: translations['common_disabled'], value: 'disabled' },
			])
		);
	}

	private updateConfigLabels(): void {
		this._translateService
			.stream([
				'common_label_notes_active',
				'common_label_notes_required',
				'common_label_notes_disabled',
				'common_label_documents_active',
				'common_label_documents_required',
				'common_label_documents_disabled',
			])
			.subscribe(translations => {
				this.noteConfigLabels = {
					active: translations['common_label_notes_active'],
					required: translations['common_label_notes_required'],
					disabled: translations['common_label_notes_disabled'],
				};

				this.documentConfigLabels = {
					active: translations['common_label_documents_active'],
					required: translations['common_label_documents_required'],
					disabled: translations['common_label_documents_disabled'],
				};
			});
	}

	/**
	 * Estrae il messaggio di errore da una risposta HTTP, gestendo il caso in cui
	 * error.error sia un Blob (quando responseType è 'blob')
	 */
	protected async extractErrorMessage(
		error: any,
		defaultMessage: string = this._translateService.instant('common_toast_error_during_operation')
	): Promise<string> {
		let errorMessage = defaultMessage;

		// Quando responseType è 'blob', error.error potrebbe essere un Blob
		let errorData = error.error;
		if (errorData instanceof Blob) {
			try {
				const text = await errorData.text();
				errorData = JSON.parse(text);
			} catch (e) {
				// Se non è JSON, usa il messaggio di default
				return errorMessage;
			}
		}

		// Gestione errori di validazione (422) e altri errori
		if (errorData?.errors) {
			if (typeof errorData.errors === 'object' && !Array.isArray(errorData.errors)) {
				// Errors è un oggetto con chiavi (field names) e array di messaggi
				const errorFields = Object.keys(errorData.errors);
				if (errorFields.length > 0) {
					// Mostra il primo errore trovato
					const firstField = errorFields[0];
					const firstError = errorData.errors[firstField];
					errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
				}
			} else if (typeof errorData.errors === 'string') {
				errorMessage = errorData.errors;
			}
		} else if (errorData?.message) {
			errorMessage = errorData.message;
		}

		return errorMessage;
	}
}
